import React, { useState, useEffect, useRef } from "react"
import { graphql } from "gatsby"
import { titleTag } from "../utils/headingLevel"
import { PrismicRichText } from "@prismicio/react"
import { Modal } from "react-bootstrap"
import { idMaker } from "../utils/idMaker"
import { fireMetaEvent } from "../utils/useMeta"

export const HeroVideo = ({ slice, index, slices }) => {
  const Title = titleTag(slices, index)
    // console.log("HeroVideo Slice: ", slice)

    const [showModal, setShowModal] = useState(false)
    const formContainerRef = useRef(null)

    const handleOpenModal = () => {
        setShowModal(true)
        fireMetaEvent("Lead")
    }
    const handleCloseModal = () => setShowModal(false)

    const videoUrl = slice.primary.video_link?.url
    const hasVideo = videoUrl && videoUrl.length > 0
    const formScriptUrl = slice.primary.form_embed_url?.url

    // Listen for form submission messages from the embedded external form script
    useEffect(() => {
        function handleMessage(event) {
            const d = event.data

            // HubSpot
            if (d?.type === "hsFormCallback" && d?.eventName === "onFormSubmit") {
                const values = d.data?.submissionValues || {}
                fireMetaEvent("Lead", {
                    email: values.email || values.EMAIL || null,
                    phone: values.phone || values.mobilephone || values.PHONE || null,
                })
                return
            }

            // Generic form submission
            if (
                typeof d === "object" &&
                d !== null &&
                (d.event === "form_submitted" || d.type === "form-submitted")
            ) {
                fireMetaEvent("Lead", {
                    email: d.email || d.data?.email || null,
                    phone: d.phone || d.data?.phone || null,
                })
            }
        }

        window.addEventListener("message", handleMessage)
        return () => window.removeEventListener("message", handleMessage)
    }, [])

    // Load form script when modal opens
    useEffect(() => {
        if (showModal && formScriptUrl && formContainerRef.current) {
            // Clear any existing content
            formContainerRef.current.innerHTML = ""

            // Create and append the script
            const script = document.createElement("script")
            script.src = formScriptUrl
            script.async = true
            script.crossOrigin = "anonymous"

            // Extract ID from URL for the script id attribute
            const urlParts = formScriptUrl.split("/")
            const formId = urlParts[urlParts.length - 2] || "form-script"
            script.id = `fs_${formId}`

            formContainerRef.current.appendChild(script)

            return () => {
                // Cleanup script when modal closes
                if (formContainerRef.current) {
                    formContainerRef.current.innerHTML = ""
                }
            }
        }
    }, [showModal, formScriptUrl])

    return (
        <div
            className={`hero_video style_number_${slice.primary.style_number1 || "1"}`}
            id={idMaker(slice.primary.title?.text || "hero-video")}
        >
            <section className="hero-video-section">
                {/* Video Background */}
                <div className="hero-video-container">
                    {/* Overlay */}
                    <div className="hero-video-overlay"></div>

                    {/* eslint-disable jsx-a11y/media-has-caption */}
                    {hasVideo ? (
                        <video
                            className="hero-video-bg"
                            autoPlay
                            loop
                            muted
                            playsInline
                            controlsList="nodownload"
                        >
                            <source src={videoUrl} type="video/mp4" />
                        </video>
                    ) : (
                        <div className="hero-video-bg-fallback"></div>
                    )}

                    {/* Content */}
                    <div className="hero-video-content">
                        <div className="hero-video-content-inner">
                            <PrismicRichText
                                field={slice.primary.title?.richText}
                                components={{
                                    heading1: ({ children }) => (
                                        <Title className="hero-video-title">{children}</Title>
                                    ),
                                    heading2: ({ children }) => (
                                        <h2 className="hero-video-title">{children}</h2>
                                    ),
                                }}
                            />
                            <PrismicRichText
                                field={slice.primary.subtitle?.richText}
                                components={{
                                    paragraph: ({ children }) => (
                                        <p className="hero-video-subtitle">{children}</p>
                                    ),
                                }}
                            />
                            {slice.primary.button_label && (
                                <button
                                    type="button"
                                    className="hero-video-cta-btn"
                                    onClick={handleOpenModal}
                                >
                                    {slice.primary.button_label}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Modal with Embedded Form */}
                <Modal
                    show={showModal}
                    onHide={handleCloseModal}
                    centered
                    size="xl"
                    className="hero-video-modal"
                    aria-labelledby="hero-video-modal-title"
                >
                    <Modal.Header closeButton />
                    <Modal.Body className="p-0">
                        <div className="hero-video-modal-layout">
                            {/* Left panel — pitch copy */}
                            <div className="hero-video-modal-pitch">
                                <h2 id="hero-video-modal-title" className="hero-video-modal-pitch-title">
                                    {slice.primary.modal_title || "Here to Simplify Your Operation"}
                                </h2>
                                <p className="hero-video-modal-pitch-subtitle">
                                    {slice.primary.modal_pitch_subtitle || "Take the hassle out of running your operations. Our team will show you how GrubPOS optimises your workflow, increases efficiency, and supports your growth."}
                                </p>
                                <p className="hero-video-modal-pitch-trust">
                                    {slice.primary.modal_trust_copy || "Over 1,000+ restaurants worldwide rely on GrubPOS."}
                                </p>
                            </div>

                            {/* Right panel — form */}
                            <div className="hero-video-modal-form-panel">
                                <div
                                    ref={formContainerRef}
                                    className="hero-video-form-container"
                                >
                                    {/* Form will be loaded here via script */}
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            </section>
        </div>
    )
}

export const query = graphql`
  fragment PageDataBodyHeroVideo on PrismicPageDataBodyHeroVideo {
    id
    slice_type
    primary {
      style_number1
      video_link {
        url
      }
      title {
        richText
        text
      }
      subtitle {
        richText
        text
      }
      button_label
      modal_title
      modal_pitch_subtitle
      modal_trust_copy
      form_embed_url {
        url
      }
    }
  }

  fragment HomepageDataBodyHeroVideo on PrismicHomepageDataBodyHeroVideo {
    id
    slice_type
    primary {
      style_number1
      video_link {
        url
      }
      title {
        richText
        text
      }
      subtitle {
        richText
        text
      }
      button_label
      modal_title
      modal_pitch_subtitle
      modal_trust_copy
      form_embed_url {
        url
      }
    }
  }
`
